import { useCallback, useRef, useState } from 'react';
import { FileChatRequest, postFileChat } from '@/hooks/apiHooks/file';
import { useTopicPipelineSSE } from '@/hooks/sse/useTopicPipelineSSE';
import { ChatMessage } from './types';
import { FILE_CHAT_EVENTS } from './constants';
import { createMessageId, getSseText } from './utils';

export const useFileChat = (
  fileUuid: string,
  passportId: string | undefined,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [responseTopic, setResponseTopic] = useState<string | null>(null);
  const pendingAssistantIdsRef = useRef(new Map<string, string>());

  const resetChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setResponseTopic(null);
    pendingAssistantIdsRef.current.clear();
  }, []);

  const updateAssistantMessage = useCallback(
    (
      aiPipelineId: string | undefined,
      updater: (message: ChatMessage) => ChatMessage,
      fallbackToLatestPending = false,
    ) => {
      const assistantId = aiPipelineId
        ? pendingAssistantIdsRef.current.get(aiPipelineId)
        : undefined;

      if (assistantId) {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? updater(msg) : msg)),
        );
        return;
      }

      if (fallbackToLatestPending) {
        setMessages((prev) => {
          const targetIndex = prev.findLastIndex(
            (msg) => msg.role === 'assistant' && msg.status === 'pending',
          );
          if (targetIndex < 0) return prev;
          return prev.map((msg, idx) =>
            idx === targetIndex ? updater(msg) : msg,
          );
        });
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (messageContent: string) => {
      if (!messageContent || !fileUuid || !passportId) return;

      const userMessageId = createMessageId();
      const assistantMessageId = createMessageId();

      setMessages((prev) => [
        ...prev,
        { id: userMessageId, role: 'user', content: messageContent },
        {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          status: 'pending',
          question: messageContent,
        },
      ]);
      setIsSubmitting(true);

      try {
        const requestData: FileChatRequest = {
          message: messageContent,
          threadId: `${passportId}-${fileUuid}`,
          ...(conversationId && { conversationId }),
        };

        const response = await postFileChat(fileUuid, requestData);
        const acceptedChat = response.data;

        if (acceptedChat?.aiPipelineId) {
          pendingAssistantIdsRef.current.set(
            acceptedChat.aiPipelineId,
            assistantMessageId,
          );
        }

        if (acceptedChat?.topic) {
          setResponseTopic(acceptedChat.topic);
        }

        if (acceptedChat?.conversationId) {
          setConversationId(acceptedChat.conversationId);
        }
      } catch {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: '질문 요청에 실패했습니다. 다시 시도해 주세요.',
                  status: 'error',
                }
              : msg,
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [fileUuid, passportId, conversationId],
  );

  useTopicPipelineSSE({
    topic: responseTopic,
    events: FILE_CHAT_EVENTS,
    enabled: Boolean(responseTopic),
    onUnhandledMessage: ({ eventName, data }) => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[FileChat SSE] unhandled event:', eventName, data);
      }
    },
    onComplete: ({ data, pipelineId }) => {
      updateAssistantMessage(pipelineId, (msg) => ({
        ...msg,
        content: getSseText(data) ?? '답변 생성이 완료되었습니다.',
        status: 'done',
      }));
      if (typeof data.payload?.conversationId === 'string') {
        setConversationId(data.payload.conversationId);
      }
      if (pipelineId) {
        pendingAssistantIdsRef.current.delete(pipelineId);
      }
    },
    onError: ({ pipelineId }) => {
      updateAssistantMessage(
        pipelineId,
        (msg) => ({
          ...msg,
          content: '답변 생성 중 오류가 발생했습니다.',
          status: 'error',
        }),
        true,
      );
      if (pipelineId) {
        pendingAssistantIdsRef.current.delete(pipelineId);
      }
    },
  });

  return {
    messages,
    isSubmitting,
    sendMessage,
    resetChat,
  };
};
